import { createElement, Fragment, useMemo, type JSX } from "react";

type HighlightedTextProps = {
    text: string;
    highlights: Record<string, string>;
    // Type of element to wrap the highlighted text in
    wrapElementType?: keyof JSX.IntrinsicElements;
};

/**
 * HighlightedText replaces template placeholders in a string (e.g. `{{name}}`)
 * with values from the `highlights` object and wraps them in a specified element.
 *
 * @component
 * @param text - The text containing template placeholders (e.g. "Hello, {{name}}!")
 * @param highlights - An object mapping placeholder names to replacement values (e.g. { name: "Alice" })
 * @param wrapElementType- The HTML element to wrap highlighted text (default: "mark")
 *
 * @example
 * // Renders: Welcome, <strong>Bob</strong>!
 * <HighlightedText
 *   text="Welcome, {{user}}!"
 *   highlights={{ user: "Bob" }}
 *   wrapElementType="strong"
 * />
 */
export const HighlightedText: React.FC<HighlightedTextProps> = ({
    text = "",
    highlights,
    wrapElementType = "mark",
}) => {
    const textWithHighlights = useMemo(() => {
        if (!text) return [];
        if (!highlights) return [text];

        const segments = text.split(/({{[a-zA-Z]+}})/g);

        if (segments.length === 1) return [text];

        return segments.map((segment, index) => {
            const placeholder = segment.match(/^{{([a-zA-Z]+)}}$/);
            if (!placeholder) return <mark key={index}>{segment}</mark>;

            const replacementText = highlights[placeholder[1]];
            if (!replacementText) return <mark key={index} />;

            return createElement(
                wrapElementType,
                { key: index, style: { fontWeight: "bold" } },
                replacementText
            );
        });
    }, [text, highlights, wrapElementType]);

    if (!highlights) return <>{text}</>;
    if (textWithHighlights.length === 0) return <></>;

    return (
        <>
            {textWithHighlights.map((part, index) => (
                <Fragment key={`${part}-${index}`}>{part}</Fragment>
            ))}
        </>
    );
};
